import React, { useRef } from 'react';
import {
  Bell,
  CheckCheck,
  Archive,
  Trash2,
  ExternalLink,
  Clock,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  FlaskConical,
  Calendar,
  Pill,
  Bed,
  FileText,
  MessageSquare,
  Server,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useArchiveNotification,
  useDeleteNotification,
} from '@/hooks/useNotifications';
import { Notification, NotificationType } from '@/types/notification';
import { useTranslation } from '@/hooks/useTranslation';
import { useDateFormat } from '@/hooks/useDateFormat';
import { getLocalizedValue } from '@/utils/localization';
import { Button } from '@/components/ui/button';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { formatDate } = useDateFormat();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: notificationsData, isLoading } = useNotifications({
    limit: 5,
    status: 'sent',
  });

  // Get unread count
  const { data: unreadCount } = useUnreadCount();

  // Mutation hooks
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const archiveNotification = useArchiveNotification();
  const deleteNotification = useDeleteNotification();

  const recentNotifications = notificationsData?.data || [];


  // NOTE: outside-click dismissal is owned by the Header, whose wrapper ref
  // spans BOTH the bell trigger and this panel. A second outside-click handler
  // here (whose ref covered only the panel, not the trigger) treated a click on
  // the bell as "outside" → it called onClose() on mousedown, then the bell's
  // own click toggled the now-closed state straight back open. That's the
  // "clicking the bell while open reopens instead of closes" bug. Leaving
  // dismissal to the Header keeps a single source of truth.

  const getNotificationIcon = (type: NotificationType) => {
    const iconClass = 'w-5 h-5';
    const strokeWidth = 2;
    switch (type) {
      case 'lab_result':
        return <FlaskConical className={iconClass} style={{ color: 'var(--color-primary-600)' }} strokeWidth={strokeWidth} />;
      case 'appointment':
        return <Calendar className={iconClass} style={{ color: 'var(--color-info)' }} strokeWidth={strokeWidth} />;
      case 'medication':
        return <Pill className={iconClass} style={{ color: 'var(--color-success)' }} strokeWidth={strokeWidth} />;
      case 'admission':
      case 'discharge':
      case 'transfer':
        return <Bed className={iconClass} style={{ color: 'var(--color-warning)' }} strokeWidth={strokeWidth} />;
      case 'order':
        return <FileText className={iconClass} style={{ color: 'var(--color-primary-600)' }} strokeWidth={strokeWidth} />;
      case 'message':
        return <MessageSquare className={iconClass} style={{ color: 'var(--color-info)' }} strokeWidth={strokeWidth} />;
      case 'system':
        return <Server className={iconClass} style={{ color: 'var(--color-text-tertiary)' }} strokeWidth={strokeWidth} />;
      case 'success':
        return <CheckCircle className={iconClass} style={{ color: 'var(--color-success)' }} strokeWidth={strokeWidth} />;
      case 'warning':
        return <AlertTriangle className={iconClass} style={{ color: 'var(--color-warning)' }} strokeWidth={strokeWidth} />;
      case 'error':
        return <AlertCircle className={iconClass} style={{ color: 'var(--color-error)' }} strokeWidth={strokeWidth} />;
      case 'info':
      default:
        return <Info className={iconClass} style={{ color: 'var(--color-info)' }} strokeWidth={strokeWidth} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'var(--color-error)';
      case 'high':
        return 'var(--color-warning)';
      case 'normal':
        return 'var(--color-info)';
      case 'low':
      default:
        return 'var(--color-text-tertiary)';
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read using React Query mutation
    if (notification.status === 'pending') {
      markAsRead.mutate(notification.id);
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const now = new Date();
    const notifDate = new Date(timestamp);
    const diffMs = now.getTime() - notifDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return language === 'en' ? 'Just now' : 'الآن';
    if (diffMins < 60) return language === 'en' ? `${diffMins}m ago` : `منذ ${diffMins} د`;
    if (diffHours < 24) return language === 'en' ? `${diffHours}h ago` : `منذ ${diffHours} س`;
    if (diffDays < 7) return language === 'en' ? `${diffDays}d ago` : `منذ ${diffDays} ي`;
    return formatDate(notifDate);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleMarkAsRead = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    markAsRead.mutate(id);
  };

  const handleArchive = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    archiveNotification.mutate(id);
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNotification.mutate(id);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="fixed sm:absolute top-16 sm:top-full right-2 sm:right-0 left-2 sm:left-auto sm:mt-2 w-[calc(100vw-1rem)] sm:w-96 max-w-md rounded-lg shadow-xl border z-50"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderColor: 'var(--color-border-primary)'
      }}
    >
      {/* Header */}
      <div className="p-3 sm:p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border-primary)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="font-semibold text-base sm:text-lg truncate" style={{ color: 'var(--color-text-primary)' }}>
            {t('notifications.title')}
          </h3>
          {(unreadCount && unreadCount > 0) ? (
            <span
              className="px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'white'
              }}
            >
              {unreadCount}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {unreadCount && unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleMarkAllAsRead}
              loading={markAllAsRead.isPending}
              title={t('notifications.markAllRead')}
              style={{ color: 'var(--color-primary-600)' }}
            >
              {!markAllAsRead.isPending && <CheckCheck />}
            </Button>
          ) : null}

          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
          >
            <X />
          </Button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="p-6 sm:p-8 text-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-primary-600)' }}></div>
            <p className="text-xs sm:text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {t('common.loading')}...
            </p>
          </div>
        ) : recentNotifications.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <Bell className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3" style={{ color: 'var(--color-text-tertiary)' }} />
            <p className="text-xs sm:text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {t('notifications.empty')}
            </p>
          </div>
        ) : (
          recentNotifications.map((notification) => (
            <div
              key={notification.id}
              className="border-b transition-colors group"
              style={{
                borderColor: 'var(--color-border-primary)',
                backgroundColor: notification.status === 'pending' ? 'var(--color-primary-50)' : 'transparent'
              }}
              onMouseEnter={(e) => {
                if (notification.status === 'read') {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                }
              }}
              onMouseLeave={(e) => {
                if (notification.status === 'read') {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="text-xs sm:text-sm font-medium line-clamp-1" style={{ color: 'var(--color-text-primary)' }}>
                        {getLocalizedValue(notification.title, language)}
                      </h4>
                      {notification.status === 'pending' && (
                        <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: 'var(--color-primary-600)' }}></span>
                      )}
                    </div>
                    <p className="text-xs line-clamp-2 mb-1.5 sm:mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      {getLocalizedValue(notification.message, language)}
                    </p>
                    <div className="flex items-center gap-2 sm:gap-3 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimestamp(notification.createdAt)}
                      </span>
                      {notification.priority !== 'normal' && (
                        <span
                          className="px-1 sm:px-1.5 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${getPriorityColor(notification.priority)}20`,
                            color: getPriorityColor(notification.priority)
                          }}
                        >
                          {notification.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex gap-0.5 sm:gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                    {notification.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={(e) => handleMarkAsRead(notification.id, e)}
                        loading={markAsRead.isPending}
                        title={t('notifications.markRead')}
                      >
                        {!markAsRead.isPending && <CheckCheck />}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      onClick={(e) => handleArchive(notification.id, e)}
                      loading={archiveNotification.isPending}
                      title={t('notifications.archive')}
                    >
                      {!archiveNotification.isPending && <Archive />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      onClick={(e) => handleDelete(notification.id, e)}
                      loading={deleteNotification.isPending}
                      title={t('notifications.delete')}
                    >
                      {!deleteNotification.isPending && <Trash2 />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      {recentNotifications.length > 0 && (
        <div className="p-2 sm:p-3 border-t" style={{ borderColor: 'var(--color-border-primary)' }}>
          <Button
            variant="ghost"
            block
            size="sm"
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            style={{ color: 'var(--color-primary-600)' }}
          >
            {t('notifications.viewAll')}
            <ExternalLink />
          </Button>
        </div>
      )}
    </div>
  );
};
